import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alerts from './Alerts';
import {
  useGetDefaultAlerts,
  useGetCustomAlerts,
  useDeleteDefaultAlert,
  useDeleteCustomAlert,
  useCreateSmartAlert,
  useCreateCustomAlert,
} from '../../utils/api-hooks/alerts';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import PageSkeleton from '../../components/PageSkeleton';

const mapOperatorToUiFormat = (operator) => {
  const map = {
    greater_than: 'greaterThan',
    less_than: 'lessThan',
    equals_to: 'equalTo',
    in_between: 'inBetween',
  };
  return map[operator] || operator || null;
};

const normalizeSelectedRegions = (raw) => {
  if (raw == null) return {};

  let value = raw;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return {};
    try {
      value = JSON.parse(trimmed);
    } catch {
      // Handle Python dict/array repr strings, e.g. "{'Argentina': ['Salta']}"
      // and common literals (True/False/None).
      const looksLikePython =
        (trimmed.startsWith('{') || trimmed.startsWith('[')) &&
        (trimmed.includes("'") || /\b(True|False|None)\b/.test(trimmed));

      if (looksLikePython) {
        try {
          const jsonish = trimmed
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false')
            .replace(/'/g, '"');
          value = JSON.parse(jsonish);
        } catch {
          // fall through to CSV fallback
        }
      }

      // If it's a comma-separated list, fallback to single "Regions" bucket
      const parts = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
      if (parts.length > 0) return { Regions: parts };
      return {};
    }
  }

  // Already in expected object shape
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const normalized = {};
    Object.entries(value).forEach(([k, v]) => {
      if (Array.isArray(v)) normalized[k] = v.filter((x) => x != null);
      else if (v == null) normalized[k] = [];
      else normalized[k] = [v];
    });
    return normalized;
  }

  // Array fallback: treat as a flat selection list
  if (Array.isArray(value)) {
    const items = value.map((x) => String(x)).filter(Boolean);
    if (items.length === 0) return {};
    return { Regions: items };
  }

  return {};
};

const getRegionsCandidate = (alert) =>
  alert?.regions ??
  alert?.region ??
  alert?.selected_regions ??
  alert?.selectedRegions ??
  alert?.selected_regions_by_country ??
  alert?.regions_by_country;

const buildLoadingView = () => <PageSkeleton shape="form" />;

const AlertEdit = () => {
  const { alertId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fontSize } = useGlobalFontSize();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const type = searchParams.get('type') === 'smart' ? 'smart' : 'custom';

  const { data: defaultAlertsData, isLoading: isLoadingDefault } = useGetDefaultAlerts();
  const { data: customAlertsData, isLoading: isLoadingCustom } = useGetCustomAlerts();

  const deleteDefaultMutation = useDeleteDefaultAlert();
  const deleteCustomMutation = useDeleteCustomAlert();
  const createSmartAlertMutation = useCreateSmartAlert();
  const createCustomAlertMutation = useCreateCustomAlert();

  const alert = useMemo(() => {
    const list = type === 'smart' ? defaultAlertsData?.alerts : customAlertsData?.alerts;
    const alerts = list || [];
    return alerts.find((a) => String(a.alert_id) === String(alertId));
  }, [type, defaultAlertsData, customAlertsData, alertId]);

  // Keep a stable reference during submit (prevents "Alert not found" flash after delete).
  const alertRef = useRef(null);
  if (alert && !alertRef.current) {
    alertRef.current = alert;
  }
  const stableAlert = alertRef.current || alert;

  const isLoading = type === 'smart' ? isLoadingDefault : isLoadingCustom;

  const initialProps = useMemo(() => {
    if (!stableAlert) return null;

    const regionsCandidate = getRegionsCandidate(stableAlert);
    const normalizedRegions = normalizeSelectedRegions(regionsCandidate);

    if (type === 'smart') {
      return {
        initialAlertType: 'smart',
        initialCurrentStep: 1,
        initialSelectedCommodity: stableAlert.commodity ?? null,
        initialSelectedSpecificAlertType: stableAlert.specific_alert_type ?? null,
        initialSelectedRegions: normalizedRegions,
      };
    }

    // custom
    const operator = mapOperatorToUiFormat(stableAlert.operator);
    const granularity =
      Array.isArray(stableAlert.granularity) && stableAlert.granularity.length > 0
        ? stableAlert.granularity[0]
        : stableAlert.granularity ?? null;

    return {
      initialAlertType: 'custom',
      initialCurrentStep: 1,
      initialSelectedCustomAlertType: stableAlert.alert_type ?? null,
      initialSelectedCommodity: stableAlert.commodity ?? null,
      initialSelectedRegions: normalizedRegions,
      initialCustomAlertConfiguration: {
        operator,
        granularity,
        value: stableAlert.value ?? '',
        minValue: stableAlert.min_value ?? '',
        maxValue: stableAlert.max_value ?? '',
      },
    };
  }, [stableAlert, type]);

  if (isLoading || isSubmitting) {
    return buildLoadingView(fontSize, isSubmitting ? 'Saving alert...' : 'Loading alert...');
  }

  if (!stableAlert || !initialProps) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-start px-4 py-10 bg-[#F0F5F2] bg-dark-app">
        <div className="flex flex-col gap-6 items-center w-full max-w-full lg:max-w-[1038px]">
          <p className="text-[#828382] dark:text-[#888888] font-['Inter']" style={{ fontSize: fontSize + 2 }}>
            Alert not found.
          </p>
          <button
            onClick={() => navigate('/alerts#enabled')}
            className="px-6 py-2 bg-bloom-green text-white rounded-lg hover:bg-bloom-green-hover transition-colors"
          >
            Back to alerts
          </button>
        </div>
      </div>
    );
  }

  return (
    <Alerts
      {...initialProps}
      pageTitle="Edit alert"
      onExit={() => navigate('/alerts#enabled')}
      submitHandlers={{
        smart: async (payload) => {
          setIsSubmitting(true);
          try {
            await deleteDefaultMutation.mutateAsync(stableAlert);
            await createSmartAlertMutation.mutateAsync(payload);
            navigate('/alerts#enabled');
          } finally {
            setIsSubmitting(false);
          }
        },
        custom: async (payload) => {
          setIsSubmitting(true);
          try {
            await deleteCustomMutation.mutateAsync(stableAlert);
            await createCustomAlertMutation.mutateAsync(payload);
            navigate('/alerts#enabled');
          } finally {
            setIsSubmitting(false);
          }
        },
      }}
    />
  );
};

export default AlertEdit;

